import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { fmtDate, PriorityBadge } from "@/lib/radar";
import { Activity, ShieldCheck } from "lucide-react";

const STATES = ["New", "Reviewed", "Qualified", "Contacted", "Engaged", "Opportunity", "Client", "Nurture", "Disqualified"];

export default function LeadActivity() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<any[]>([]);
  const [audit, setAudit] = useState<any[] | null>(null);

  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data));
    api.get("/audit", { params: { limit: 60 } }).then((r) => setAudit(r.data)).catch(() => setAudit(null));
  }, []);

  const byState: Record<string, any[]> = {};
  companies.forEach((c) => { (byState[c.lead_status] = byState[c.lead_status] || []).push(c); });

  return (
    <div className="space-y-5" data-testid="lead-activity-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2"><Activity className="h-5 w-5 text-teal-700" /> Lead Activity & Feedback</h1>
        <p className="text-sm text-slate-500">Internal workflow pipeline. Contact/outreach is manual — no automated sending in Phase 01.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-9 gap-3">
        {STATES.map((st) => (
          <Card key={st} className="p-3 border-slate-200 shadow-none">
            <div className="num text-2xl font-semibold text-slate-900">{(byState[st] || []).length}</div>
            <div className="text-[11px] uppercase tracking-wide text-slate-500">{st}</div>
          </Card>
        ))}
      </div>

      <Card className="p-0 border-slate-200 shadow-none overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200"><h2 className="font-heading font-semibold text-slate-800">Pipeline</h2></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-500">
              <th className="text-left font-medium px-4 py-2">Company</th>
              <th className="text-left font-medium px-3 py-2">Lead Status</th>
              <th className="text-left font-medium px-3 py-2">Priority</th>
              <th className="text-right font-medium px-3 py-2">Opp. Score</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {companies.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/companies/${c.id}`)} data-testid={`activity-row-${c.id}`}>
                  <td className="px-4 py-2 font-medium text-slate-800">{c.company_name}</td>
                  <td className="px-3 py-2 text-slate-600">{c.lead_status}</td>
                  <td className="px-3 py-2"><PriorityBadge value={c.priority} /></td>
                  <td className="px-3 py-2 text-right num text-slate-700">{c.opportunity_score}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {audit && (
        <Card className="p-0 border-slate-200 shadow-none overflow-hidden" data-testid="audit-trail">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-teal-700" /><h2 className="font-heading font-semibold text-slate-800">Audit Trail</h2></div>
          <div className="max-h-[360px] overflow-auto divide-y divide-slate-100">
            {audit.map((a) => (
              <div key={a.id} className="px-4 py-2 flex items-center justify-between text-sm">
                <div>
                  <span className="font-medium text-slate-800">{a.event_type}</span>
                  <span className="text-slate-400 text-xs ml-2">{a.user_email} ({a.role})</span>
                </div>
                <span className="text-[11px] text-slate-400 num">{fmtDate(a.created_at)}</span>
              </div>
            ))}
            {audit.length === 0 && <div className="px-4 py-6 text-sm text-slate-400">No audit events.</div>}
          </div>
        </Card>
      )}
    </div>
  );
}
